import { z } from "zod";

import {
  ANALYSIS_PRIORITIES,
  MAX_BULK_ANALYSIS_IDS,
} from "@/lib/constants/analysis";
import {
  PERCENTAGE_PRECISION_MAX,
  PERCENTAGE_PRECISION_MIN,
} from "@/lib/utils/sieve-sizes";
import { uuidSchema } from "@/lib/validation/schemas/common";

/**
 * Analysis Validation Schemas
 *
 * Zod schemas for analysis forms including gradation, wash loss, and general analysis forms.
 * Backend validation (FastAPI/Pydantic) is the source of truth.
 * These schemas provide client-side UX validation.
 */

// =============================================================================
// Common Types
// =============================================================================

/**
 * Mass unit schema
 */
export const massUnitSchema = z.enum(["g", "kg", "lb"]);
export type MassUnit = z.infer<typeof massUnitSchema>;

/**
 * Gradation standard schema
 */
export const gradationStandardSchema = z.enum(["ASTM", "CSA"]);
export type GradationStandard = z.infer<typeof gradationStandardSchema>;

/**
 * Input format schema for gradation data entry
 */
export const inputFormatSchema = z.enum([
  "retained_mass",
  "cum_retained_mass",
  "pc_ind_retained",
  "pc_retained",
  "pc_passing",
]);
export type InputFormat = z.infer<typeof inputFormatSchema>;

// =============================================================================
// Sieve Row Schema
// =============================================================================

/**
 * Sieve row schema for gradation table data
 */
export const sieveRowSchema = z.object({
  sieve_id: z.string().min(1, "Sieve ID is required"),
  sieve_size: z.string().min(1, "Sieve size is required"),
  sieve_dia_mm: z.number().nonnegative("Opening must be non-negative"),
  retained_mass: z.number().nullable(),
  cum_retained_mass: z.number().nullable(),
  pc_ind_retained: z.number().nullable(),
  pc_retained: z.number().nullable(),
  pc_passing: z.number().nullable(),
  /** Procedure reporting precision for % columns (0–4). Omit/null → default 2 at display. */
  percentage_precision: z
    .number()
    .int("Percentage precision must be an integer")
    .min(
      PERCENTAGE_PRECISION_MIN,
      `Percentage precision must be at least ${PERCENTAGE_PRECISION_MIN}`,
    )
    .max(
      PERCENTAGE_PRECISION_MAX,
      `Percentage precision must be at most ${PERCENTAGE_PRECISION_MAX}`,
    )
    .nullable()
    .optional(),
});

export type SieveRowForm = z.infer<typeof sieveRowSchema>;

// =============================================================================
// Wash Loss Schema
// =============================================================================

/**
 * Wash loss form schema
 */
export const washLossFormSchema = z.object({
  washMass: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const num = parseFloat(val);
        return Number.isFinite(num) && num >= 0;
      },
      { message: "Wash mass must be a positive number" },
    ),
  washLossMass: z.string().optional(),
  washLossPc: z.string().optional(),
  washMethod: z.string().nullable(),
  washLossProcedure: z.string().nullable(),
});

export type WashLossForm = z.infer<typeof washLossFormSchema>;

// =============================================================================
// Gradation Form Schema
// =============================================================================

/**
 * Gradation form schema - used for client-side validation
 * All fields are optional to allow incremental saving
 */
export const gradationFormSchema = z.object({
  standard: gradationStandardSchema.default("ASTM"),
  procedureName: z.string().nullable(),
  sieveFrameSize: z.string().nullable(),
  finalTotalMass: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const num = parseFloat(val);
        return Number.isFinite(num) && num > 0;
      },
      { message: "Dry mass must be a positive number" },
    ),
  moistMass: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const num = parseFloat(val);
        return Number.isFinite(num) && num > 0;
      },
      { message: "Moist mass must be a positive number" },
    ),
  massUnit: massUnitSchema.default("g"),
  washLossMass: z.string().optional(),
  isWashLoss: z.boolean().default(false),
  washData: washLossFormSchema.optional(),
  selectedSieves: z.array(z.string()).default([]),
  rows: z.array(sieveRowSchema).default([]),
  inputFormat: inputFormatSchema.nullable().default("retained_mass"),
  calculationMethod: z.string().optional(),
  notes: z
    .string()
    .max(2000, "Notes must be less than 2000 characters")
    .optional(),
  hasUnsavedChanges: z.boolean().default(false),
});

export type GradationForm = z.infer<typeof gradationFormSchema>;

// =============================================================================
// Gradation Payload Schema (for API submission)
// =============================================================================

/**
 * Gradation table row schema for API payload
 */
export const gradationTableRowSchema = z.object({
  sieve_id: z.string(),
  sieve_size: z.string(),
  sieve_dia_mm: z.number(),
  retained_mass: z.number().optional(),
  cum_retained_mass: z.number().optional(),
  pc_ind_retained: z.number().optional(),
  pc_retained: z.number().optional(),
  pc_passing: z.number().optional(),
});

/**
 * Gradation API payload schema
 *
 * NOTE: 'standard' field intentionally NOT included - backend does not accept it.
 * The standard is derived from the procedure name by the backend.
 */
export const gradationPayloadSchema = z
  .object({
    // NOTE: 'standard' field removed - backend rejects it with "extra inputs not permitted"
    procedure: z.string().optional(),
    dry_mass: z.number().nullable(),
    moist_mass: z.number().optional(),
    user_entered_unit: massUnitSchema.optional(),
    sieve_frame_size: z.string().optional(),
    is_wash_loss: z.boolean(),
    wash_mass: z.number().optional(),
    wash_method: z.string().optional(),
    wash_loss_procedure: z.string().optional(),
    notes: z.string().nullable().optional(),
    gradation_table: z.array(gradationTableRowSchema).optional(),
  })
  .refine(
    (data) => {
      // If gradation_table has any rows with mass data, dry_mass is required
      const hasWeightData = data.gradation_table?.some(
        (row) =>
          row.retained_mass !== undefined ||
          row.cum_retained_mass !== undefined,
      );
      if (
        hasWeightData &&
        (data.dry_mass === null || data.dry_mass === undefined)
      ) {
        return false;
      }
      return true;
    },
    {
      message:
        "Dry mass is required when weight data is present in the gradation table",
      path: ["dry_mass"],
    },
  );

export type GradationPayload = z.infer<typeof gradationPayloadSchema>;

// =============================================================================
// Analysis Creation Schema
// =============================================================================

/**
 * New analysis creation schema
 */
export const newAnalysisFormSchema = z.object({
  analysis_type_id: z.string().min(1, "Analysis type is required"),
  priority: z.string().optional(),
  lab_id: z.string().optional(),
  tested_by_user_id: z.string().optional(),
  is_test: z.boolean().optional(),
});

export type NewAnalysisForm = z.infer<typeof newAnalysisFormSchema>;

// =============================================================================
// Analysis Sign-Off Schema
// =============================================================================

/**
 * Analysis sign-off form schema
 */
export const analysisSignOffSchema = z.object({
  notes: z
    .string()
    .max(2000, "Notes must be less than 2000 characters")
    .optional(),
  location_aggregate_ids: z.array(z.string()).default([]),
  effective_from: z.iso.datetime({ offset: true }).optional(),
});

export type AnalysisSignOffForm = z.infer<typeof analysisSignOffSchema>;

// =============================================================================
// Analysis Unlock Schema
// =============================================================================

/**
 * Analysis unlock form schema
 */
export const analysisUnlockSchema = z.object({
  reason: z
    .string()
    .min(1, "Reason is required for unlocking")
    .max(500, "Reason must be less than 500 characters"),
});

export type AnalysisUnlockForm = z.infer<typeof analysisUnlockSchema>;

// =============================================================================
// Analysis Evaluation Refresh Schema
// =============================================================================

/**
 * Evaluation refresh request — v1 UI sends scope only (API defaults nested to latest).
 */
export const analysisEvaluationRefreshSchema = z.object({
  scope: z.enum(["both", "specification", "procedure"]).default("both"),
});

export type AnalysisEvaluationRefreshForm = z.infer<
  typeof analysisEvaluationRefreshSchema
>;

// =============================================================================
// Analysis Archive Schema
// =============================================================================

/**
 * Analysis archive form schema (API v2.3.47+)
 */
export const analysisArchiveSchema = z.object({
  reason: z
    .string()
    .max(500, "Reason must be less than 500 characters")
    .optional(),
});

export type AnalysisArchiveForm = z.infer<typeof analysisArchiveSchema>;

// =============================================================================
// Static Imaging Images Patch Schema
// =============================================================================

/**
 * One capture include/exclude update.
 * `reason_for_exclusion` is required when excluding (trimmed, max 500).
 */
export const staticImagingImagePatchItemSchema = z
  .object({
    image_id: z.string().uuid("Invalid image ID"),
    exclude_from_analysis: z.boolean(),
    reason_for_exclusion: z
      .string()
      .trim()
      .max(500, "Reason must be less than 500 characters")
      .nullable()
      .optional(),
  })
  .strict()
  .superRefine((item, ctx) => {
    if (!item.exclude_from_analysis) return;
    const reason = item.reason_for_exclusion?.trim() ?? "";
    if (!reason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reason_for_exclusion"],
        message: "Reason is required when excluding an image",
      });
    }
  });

/**
 * PATCH /api/v1/analyses/{analysis_id}/static-imaging/images body:
 * a JSON array of items (OpenAPI minItems 1, maxItems 100).
 */
export const staticImagingImagesPatchSchema = z
  .array(staticImagingImagePatchItemSchema)
  .min(1, "At least one image is required")
  .max(100, "At most 100 images can be updated at once");

export type StaticImagingImagesPatchForm = z.infer<
  typeof staticImagingImagesPatchSchema
>;

// =============================================================================
// Bulk update / workflow (OpenAPI v2.3.107)
// =============================================================================

const analysisIdsSchema = z
  .array(uuidSchema)
  .min(1, "At least one analysis ID is required")
  .max(
    MAX_BULK_ANALYSIS_IDS,
    `Cannot update more than ${MAX_BULK_ANALYSIS_IDS} analyses at once`,
  )
  .refine((ids) => new Set(ids).size === ids.length, {
    message: "Duplicate analysis IDs are not allowed",
  });

const optionalNotes500 = z
  .string()
  .max(500, "Notes must be less than 500 characters")
  .optional();

const BULK_UPDATE_PATCH_KEYS = [
  "priority",
  "lab_id",
  "tested_by_user_id",
  "sample_preparation",
  "specification_id",
  "procedure_id",
] as const;

/** API payload for PATCH /api/v1/analyses/list (OpenAPI AnalysisBulkUpdate). */
export const analysisBulkUpdateSchema = z
  .object({
    analysis_ids: analysisIdsSchema,
    priority: z.enum(ANALYSIS_PRIORITIES).optional(),
    lab_id: uuidSchema.optional(),
    tested_by_user_id: uuidSchema.optional(),
    sample_preparation: z
      .string()
      .max(255, "Sample preparation must be less than 255 characters")
      .optional(),
    specification_id: uuidSchema.optional(),
    procedure_id: uuidSchema.optional(),
  })
  .superRefine((data, ctx) => {
    const hasAnyPatchField = BULK_UPDATE_PATCH_KEYS.some(
      (key) => data[key] !== undefined,
    );
    if (!hasAnyPatchField) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "At least one analysis field is required",
        path: ["analysis_ids"],
      });
    }
  });

export type AnalysisBulkUpdateInput = z.infer<typeof analysisBulkUpdateSchema>;

/** Bulk-edit modal form: empty string means leave the field unchanged. */
export const analysisBulkEditFormSchema = z
  .object({
    priority: z.string(),
    lab_id: z.string(),
    tested_by_user_id: z.string(),
    sample_preparation: z.string(),
    specification_id: z.string(),
    procedure_id: z.string(),
  })
  .superRefine((data, ctx) => {
    const hasAnyPatchField = BULK_UPDATE_PATCH_KEYS.some((key) =>
      Boolean(data[key]?.trim()),
    );
    if (!hasAnyPatchField) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Fill at least one field to apply",
        path: ["priority"],
      });
    }
  });

export type AnalysisBulkEditForm = z.infer<typeof analysisBulkEditFormSchema>;

export const analysisBulkRequestSignOffSchema = z.object({
  action: z.literal("request_sign_off"),
  analysis_ids: analysisIdsSchema,
  sign_off_requested_for_user_id: uuidSchema,
  notes: optionalNotes500,
});

export const analysisBulkCancelSignOffSchema = z.object({
  action: z.literal("cancel_sign_off_request"),
  analysis_ids: analysisIdsSchema,
  reason: optionalNotes500,
});

export const analysisBulkSignOffSchema = z.object({
  action: z.literal("sign_off"),
  analysis_ids: analysisIdsSchema,
  notes: optionalNotes500,
  location_aggregate_ids: z.array(uuidSchema).optional(),
  effective_from: z.iso.datetime({ offset: true }).optional(),
  sync_to_external: z.boolean().optional(),
});

export const analysisBulkUnlockSchema = z.object({
  action: z.literal("unlock"),
  analysis_ids: analysisIdsSchema,
  reason: z
    .string()
    .min(1, "Reason is required for unlocking")
    .max(500, "Reason must be less than 500 characters"),
});

export const analysisBulkWorkflowSchema = z.discriminatedUnion("action", [
  analysisBulkRequestSignOffSchema,
  analysisBulkCancelSignOffSchema,
  analysisBulkSignOffSchema,
  analysisBulkUnlockSchema,
]);

export type AnalysisBulkWorkflowInput = z.infer<
  typeof analysisBulkWorkflowSchema
>;
